import {LocalDate} from '@js-joda/core';
import Hidden from '@material-ui/core/Hidden';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableFooter from '@material-ui/core/TableFooter';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import React, {useMemo} from 'react';
import {TimeEntry} from '../../pages/TimeEntries';
import {DateRange} from './DateRangeSelect';
import GapRow from './GapRow';
import TimeEntryDateFooter from './TimeEntryDateFooter';
import TimeEntryDateHeader from './TimeEntryDateHeader';
import TimeEntryRow from './TimeEntryRow';
import TimeEntryTotalsRow from './TimeEntryTotalsRow';

const useStyles = makeStyles(theme => ({
    tableHead: {
        backgroundColor: theme.palette.primary.main,
        '& th': {
            color: theme.palette.primary.contrastText,
        },
    },
    tableFooter: {
        backgroundColor: theme.palette.primary.main,
        '& td': {
            color: theme.palette.primary.contrastText,
            fontWeight: 500,
            fontSize: theme.typography.fontSize,
        },
    },
    startTimeCell: {
        width: 110,
    },
    endTimeCell: {
        width: 110,
    },
    billableHoursCell: {
        width: 60,
        paddingRight: 0,
    },
    totalHoursCell: {
        width: 60,
    },
    actionsCell: {
        width: 176,
    },
}));

type Props = {
    timeEntries : TimeEntry[];
    onDateRangeChange : (dateRange : DateRange) => void;
    onInsert : (timeEntry : TimeEntry) => Promise<void>;
    onUpdate : (timeEntry : TimeEntry) => Promise<void>;
    onDelete : (timeEntry : TimeEntry) => Promise<boolean>;
    onUnsubmit : (timeEntry : TimeEntry) => Promise<void>;
};

const TimeEntryTable : React.FC<Props> = ({timeEntries, onDateRangeChange, onInsert, onUpdate, onDelete, onUnsubmit} : Props) => {
    const classes = useStyles();
    const dates = useMemo(() => groupTimeEntries(timeEntries), [timeEntries]);
    const totalTime = getTotalTime(timeEntries);

    return (
        <Table size="small">
            <TableHead className={classes.tableHead}>
                <TableRow>
                    <TableCell>Task</TableCell>
                    <Hidden smDown>
                        <TableCell/>
                        <TableCell className={classes.startTimeCell}>Start time</TableCell>
                        <TableCell className={classes.endTimeCell}>End time</TableCell>
                    </Hidden>
                    <TableCell colSpan={2}>Hours</TableCell>
                    <TableCell className={classes.actionsCell}>Actions</TableCell>
                </TableRow>
                {dates.length > 0 && (
                    <TimeEntryTotalsRow totalTime={totalTime}/>
                )}
            </TableHead>
            <TableBody>
                {dates.length === 0 && (
                    <TableRow>
                        <TableCell colSpan={6}>
                            No time entries found in range.
                        </TableCell>
                    </TableRow>
                )}
                {dates.map(date => {
                    let previousTimeEntry : TimeEntry | null = null;

                    return (
                        <React.Fragment key={date.date.toString()}>
                            <TimeEntryDateHeader
                                date={date.date}
                                timeEntries={date.timeEntries}
                                onDateRangeChange={onDateRangeChange}
                                onInsert={onInsert}
                            />
                            {date.timeEntries.sort((a, b) => {
                                if (a.startTime !== null && b.startTime !== null) {
                                    return a.startTime.compareTo(b.startTime);
                                }

                                if (a.startTime !== null) {
                                    return -1;
                                }

                                if (b.startTime !== null) {
                                    return 1;
                                }

                                return a.id - b.id;
                            }).map(timeEntry => {
                                const row = (
                                    <React.Fragment key={timeEntry.id}>
                                        {previousTimeEntry && (
                                            <GapRow
                                                onInsert={onInsert}
                                                previousTimeEntry={previousTimeEntry}
                                                nextTimeEntry={timeEntry}
                                            />
                                        )}
                                        <TimeEntryRow
                                            timeEntry={timeEntry}
                                            onInsert={onInsert}
                                            onUpdate={onUpdate}
                                            onDelete={onDelete}
                                            onUnsubmit={onUnsubmit}
                                        />
                                    </React.Fragment>
                                );

                                previousTimeEntry = timeEntry;

                                return row;
                            })}
                            <TimeEntryDateFooter date={date.date} timeEntries={date.timeEntries} onInsert={onInsert}/>
                        </React.Fragment>
                    );
                })}
            </TableBody>
            {dates.length > 0 && (
                <TableFooter className={classes.tableFooter}>
                    <TimeEntryTotalsRow totalTime={totalTime}/>
                </TableFooter>
            )}
        </Table>
    );
};

type TimeResult = {
    total : number;
    billable : number;
    nonBillable : number;
};

export const getTotalTime = (timeEntries : TimeEntry[]) : TimeResult => timeEntries.reduce<{
    total : number,
    billable : number,
    nonBillable : number
}>((accumulator, timeEntry : TimeEntry) => {
    const minutes = getMinutes(timeEntry);
    accumulator.total += minutes.total;
    accumulator.billable += minutes.billable;
    accumulator.nonBillable += minutes.nonBillable;
    return accumulator;
}, {total: 0, billable: 0, nonBillable: 0});

export const getMinutes = (timeEntry : TimeEntry) : TimeResult => {
    if (!timeEntry.minutes) {
        return {
            total: 0,
            billable: 0,
            nonBillable: 0,
        };
    }

    if (!timeEntry.task || !timeEntry.task.billable || timeEntry.excludeFromBilling) {
        return {
            total: timeEntry.minutes,
            billable: 0,
            nonBillable: timeEntry.minutes,
        };
    }

    if (timeEntry.billableMinutesOverride !== null) {
        return {
            total: timeEntry.minutes,
            billable: timeEntry.billableMinutesOverride,
            nonBillable: timeEntry.minutes - timeEntry.billableMinutesOverride,
        };
    }

    return {
        total: timeEntry.minutes,
        billable: timeEntry.minutes,
        nonBillable: 0,
    };
};

const groupTimeEntries = (timeEntries : TimeEntry[]) => {
    const dates = [];
    let date : {date : LocalDate, timeEntries : TimeEntry[]} | null = null;

    for (const timeEntry of timeEntries.sort((a, b) => a.startDate.compareTo(b.startDate))) {
        if (date === null || !timeEntry.startDate.isEqual(date.date)) {
            date = {date: timeEntry.startDate, timeEntries: []};
            dates.push(date);
        }

        date.timeEntries.push(timeEntry);
    }

    return dates;
};

export default TimeEntryTable;
